
import { supabase } from '@/integrations/supabase/client';
import { analysisCacheService } from './analysis';
import { usageTrackingService } from './usageTracking';
import posthog from 'posthog-js';

export interface AnalysisResult {
  themes: string[];
  emotions: string[];
  painPoints: string[];
  insights: string[];
  opportunities: string[];
  recommendations: string[];
}

export const analyzeNotes = async (
  notes: Array<{ type: string; content: string; created_at?: string }>, 
  questions?: string[],
  noteSpaceIds?: string[]
): Promise<AnalysisResult> => {
  let startTime = Date.now();
  
  try {
    console.log('🔍 analyzeNotes called with:', { 
      notesCount: notes.length, 
      questionsCount: questions?.length || 0, 
      noteSpaceIds: noteSpaceIds 
    });
    
    // Check usage limits first
    const { canRun, currentUsage, limit } = await usageTrackingService.checkCanRunAnalysis();
    if (!canRun) {
      throw new Error(`Monthly analysis limit reached (${currentUsage}/${limit}). Limit resets next month.`);
    }
    
    // Check cache first if noteSpaceIds are provided
    if (noteSpaceIds && noteSpaceIds.length > 0) {
      console.log('✅ Checking for cached analysis...');
      
      // Extract note IDs for simple caching
      const noteIds = notes.map((_, index) => `note-${index}`);
      
      // Try to get last modified time from note space
      const { data: noteSpaceData } = await supabase
        .from('note_spaces')
        .select('updated_at')
        .in('id', noteSpaceIds)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      const lastModified = noteSpaceData?.updated_at;
      
      const cachedResult = await analysisCacheService.getCachedAnalysis(noteIds, noteSpaceIds, lastModified, questions);
      if (cachedResult) {
        console.log('🎯 Using cached analysis results');
        return cachedResult as AnalysisResult;
      }
      console.log('❌ No valid cache found, proceeding with AI analysis...');
    } else {
      console.log('⚠️ No noteSpaceIds provided, skipping cache check');
    }
    
    console.log('Calling AI analysis via Supabase Edge Function...');
    
    // Track LLM call start
    startTime = Date.now();
    posthog.capture('llm_call_started', {
      model: 'gpt-4.1-2025-04-14',
      provider: 'openai',
      input_length: notes.length,
      questions_count: questions?.length || 0,
      type: 'analysis',
      start_time: startTime
    });

    const { data, error } = await supabase.functions.invoke('ai-analysis', {
      body: { notes, questions: questions || [] }
    });

    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(`Analysis failed: ${error.message}`);
    }

    if (!data) {
      throw new Error('No data received from analysis service');
    }

    console.log('Analysis completed successfully');
    
    // Track LLM call completion
    const endTime = Date.now();
    posthog.capture('llm_call_completed', {
      model: 'gpt-4.1-2025-04-14',
      provider: 'openai',
      input_length: notes.length,
      questions_count: questions?.length || 0,
      output_size: JSON.stringify(data).length,
      start_time: startTime,
      end_time: endTime,
      response_time: endTime - startTime,
      type: 'analysis',
      success: true,
      themes_count: data.themes?.length || 0,
      insights_count: data.insights?.length || 0
    });
    
    // Increment usage counter
    await usageTrackingService.incrementUsage();
    
    // Cache the results if noteSpaceIds are provided
    if (noteSpaceIds && noteSpaceIds.length > 0) {
      console.log('💾 Caching analysis results...');
      
      // Extract note IDs for simple caching  
      const noteIds = notes.map((_, index) => `note-${index}`);
      
      // Get last modified time from note space
      const { data: noteSpaceData } = await supabase
        .from('note_spaces')
        .select('updated_at')
        .in('id', noteSpaceIds)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      const lastModified = noteSpaceData?.updated_at;
      
      await analysisCacheService.setCachedAnalysis(noteIds, noteSpaceIds, data, lastModified, questions);
    }
    
    return data as AnalysisResult;
    
  } catch (error) {
    console.error('Error calling AI analysis:', error);
    
    // Track LLM call failure
    const endTime = Date.now();
    posthog.capture('llm_call_failed', {
      model: 'gpt-4.1-2025-04-14',
      provider: 'openai',
      input_length: notes.length,
      questions_count: questions?.length || 0,
      start_time: startTime,
      end_time: endTime,
      response_time: endTime - startTime,
      type: 'analysis',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    
    throw error;
  }
};
